import { useEffect, useState } from 'react';
import { AlertCircle, BarChart3, Loader2, RefreshCw } from 'lucide-react';
import { api, extractErrorMessage } from '../../lib/api';
import { usePermissions } from '../../hooks/usePermissions';

/**
 * Panneau KPI service (US-SVC-08).
 *
 *  - Sélecteur de période (from / to en YYYY-MM, par défaut 12 derniers mois).
 *  - Tableau périodique : Période · Nb int · Tps moy résolution · Taux dispo
 *    · Taux panne · Préventives · Curatives · Coût total maintenance.
 *  - Bouton "Recalculer la période courante" → POST `/services/:id/kpis/recompute`.
 */

interface ServiceKpiRow {
  id: string;
  service_id: string;
  periode: string;
  nombre_interventions: number;
  temps_moyen_resolution: number | null;
  taux_disponibilite: number | null;
  taux_panne: number | null;
  mtbf: number | null;
  mttr: number | null;
  cout_total_maintenance: number | null;
  taux_preventif_realise: number | null;
  /** Pas stockés en DB — uniquement présents si la ligne sort d'un recompute. */
  nb_interventions_preventives?: number;
  nb_interventions_curatives?: number;
}

export function ServiceKpisPanel({ serviceId }: { serviceId: string }) {
  const { can } = usePermissions();
  const canUpdate = can('services', 'update');

  // Bornes par défaut : 12 derniers mois (inclus mois courant).
  const today = new Date();
  const defaultTo = formatPeriode(today);
  const defaultFrom = formatPeriode(addMonths(today, -11));

  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [rows, setRows] = useState<ServiceKpiRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setErrorMessage(null);
    try {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      const res = await api.get<{ data: ServiceKpiRow[] }>(
        `/services/${serviceId}/kpis?${params.toString()}`,
      );
      setRows(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, from, to]);

  async function handleRecompute() {
    setRecomputing(true);
    setErrorMessage(null);
    try {
      const periode = formatPeriode(new Date());
      await api.post(
        `/services/${serviceId}/kpis/recompute?periode=${encodeURIComponent(periode)}`,
      );
      await load();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setRecomputing(false);
    }
  }

  return (
    <div className="space-y-3">
      {/* Sélecteurs */}
      <div className="bg-gray-50 rounded-lg p-3 flex items-end justify-between flex-wrap gap-3">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Période début</label>
            <input
              type="month"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Période fin</label>
            <input
              type="month"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="inline-flex items-center p-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50"
            title="Rafraîchir"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          {canUpdate && (
            <button
              onClick={handleRecompute}
              disabled={recomputing}
              className="inline-flex items-center px-3 py-1.5 border border-primary-300 bg-primary-50 text-primary-700 rounded-lg text-xs font-medium hover:bg-primary-100 disabled:opacity-50"
            >
              {recomputing ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <BarChart3 className="w-3.5 h-3.5 mr-1.5" />
              )}
              Recalculer la période courante
            </button>
          )}
        </div>
      </div>

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {/* Tableau */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <Th>Période</Th>
                <Th align="right">Nb interv.</Th>
                <Th align="right">Tps moy. résolution (h)</Th>
                <Th align="right">Taux dispo (%)</Th>
                <Th align="right">Taux panne (%)</Th>
                <Th align="right">Préventives</Th>
                <Th align="right">Curatives</Th>
                <Th align="right">Coût total maintenance</Th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-500 mx-auto" />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500">
                    <BarChart3 className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                    Aucun KPI calculé pour cette période.
                    {canUpdate && (
                      <p className="mt-1 text-xs">
                        Cliquez sur « Recalculer la période courante » pour générer les indicateurs
                        du mois en cours.
                      </p>
                    )}
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">
                      {r.periode}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">{r.nombre_interventions}</td>
                    <td className="px-3 py-2 text-right text-gray-700">
                      {formatNumber(r.temps_moyen_resolution, 2)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">
                      {formatNumber(r.taux_disponibilite, 2)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">
                      {formatNumber(r.taux_panne, 2)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">
                      {r.nb_interventions_preventives ?? <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">
                      {r.nb_interventions_curatives ?? <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700 whitespace-nowrap">
                      {r.cout_total_maintenance === null
                        ? '—'
                        : formatCurrency(r.cout_total_maintenance)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-gray-400">
        Note : les colonnes Préventives / Curatives ne sont renseignées que pour les périodes
        recalculées depuis cette UI (US-SVC-08). Les KPIs sont sinon mis à jour automatiquement par
        le worker mensuel.
      </p>
    </div>
  );
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      className={`px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      {children}
    </th>
  );
}

function formatNumber(value: number | null, digits = 2): string {
  if (value === null || value === undefined) return '—';
  return Number(value).toFixed(digits);
}

function formatCurrency(value: number): string {
  try {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${value.toFixed(2)} €`;
  }
}

function formatPeriode(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function addMonths(d: Date, n: number): Date {
  const copy = new Date(d.getFullYear(), d.getMonth() + n, 1);
  return copy;
}
